import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';

import { getMyConsents, revokeMyConsent } from '../../services/kvkkService';
import { formatDateTime } from '../../utils/Utils';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Button from '../../components/ui/Button';

// Rızalarım — KVKK md. 7: rıza her zaman geri çekilebilir. Backend uçları hazırdı
// (v1/my-consents), ekran yoktu; rıza yalnız yönetici tarafından geri çekilebiliyordu.
// Rıza VERME ekranı yok: aydınlatma metni sürümü yönetilmiyor, bağlamsız rıza anlamsız.
// Kart düzeni MyPolicyTasksPage'in aynısı (spec §4.1).

const Container = styled.div``;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const IconCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.primary.main + '15'};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #004242;
  margin-bottom: 4px;
`;

const CardMeta = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  overflow-wrap: anywhere;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  margin: 6px 0 10px;
  background: ${({ $state }) => ($state === 'active' ? '#16a34a15' : '#64748b15')};
  color: ${({ $state }) => ($state === 'active' ? '#16a34a' : '#64748b')};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
`;

const EmptyHint = styled.div`
  margin-top: 8px;
  font-size: 12px;
`;

// Backend kapsam değerleri snake_case (KvkkConsentDataAccess.IsValidScope), çeviri anahtarları
// camelCase. Eşlemesiz kalan bir kapsamda anahtarın KENDİSİ yerine genel etiket gösterilir.
// Test, backend'deki listenin tamamının burada olduğunu sabitler.
export const SCOPE_KEY = {
  employment_records: 'employmentRecords',
  health: 'health',
  communication: 'communication',
  financial: 'financial',
  alumni: 'alumni',
};

// Geri çekme gerekçesi serbest metindir; bilinen sistem kodları çevrilir (snake_case jargon
// kullanıcıya gösterilmez), diğer her değer olduğu gibi yazılır.
//   employee_request — bu sayfanın sabit gerekçesi; admin_action — yönetici KVKK Rızaları sayfası.
const REASON_KEY = {
  employee_request: 'myConsents.reasonCode.employeeRequest',
  admin_action: 'myConsents.reasonCode.adminAction',
};

// 'superseded' yönetici sayfasıyla aynı türetme: revokeReason === 'superseded'.
const stateOf = (c) => {
  if (c.revokeReason === 'superseded') return 'superseded';
  return c.revokedAt ? 'revoked' : 'active';
};

export default function MyConsentsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [consents, setConsents] = useState([]);
  // 'loading' | 'ready' | 'empty' | 'unavailable' — "yüklenemedi" ile "boş" AYRI durumlardır
  // (api.js GET'te reddetmez; sinyal res.data.status).
  const [state, setState] = useState('loading');
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(() => {
    setState('loading');
    getMyConsents().then((res) => {
      const body = res?.data;
      if (body?.status) {
        const items = Array.isArray(body.data) ? body.data : (body.data?.data || []);
        setConsents(items);
        setState(items.length === 0 ? 'empty' : 'ready');
        return;
      }
      // Kişi kaydı yoksa 404 gelir (servis gövdeyi geçirir, toast'ı susturur): bu bir hata
      // değil, "rıza yok" boş durumudur.
      setConsents([]);
      setState(body?.message === 'kvkk.consent.personNotFound' ? 'empty' : 'unavailable');
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const scopeLabel = useCallback(
    (scope) => (SCOPE_KEY[scope] ? t(`kvkkConsent.scope.${SCOPE_KEY[scope]}`) : t('myConsents.otherScope')),
    [t],
  );

  const reasonLabel = useCallback((reason) => (REASON_KEY[reason] ? t(REASON_KEY[reason]) : reason), [t]);

  const handleRevokeConfirm = useCallback(async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeMyConsent(revokeTarget.id, 'employee_request');
    } catch {
      // Hata toast'ı interceptor'da. Diyalog yine kapanır ve liste tazelenir: 409
      // (zaten geri çekilmiş) sonrası kartın güncel durumu görünmeli, diyalog tekrar
      // denemeye açık kalmamalı.
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
      load();
    }
  }, [revokeTarget, load]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={!!revokeTarget}
        title={t('myConsents.revokeTitle')}
        message={t('myConsents.revokeMessage', { scope: revokeTarget ? scopeLabel(revokeTarget.scope) : '' })}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevokeTarget(null)}
        loading={revoking}
      />

      <Header>
        <Title>{t('myConsents.title')}</Title>
      </Header>

      {state === 'loading' && <EmptyState>{t('common.loading')}</EmptyState>}
      {state === 'unavailable' && <EmptyState>{t('myConsents.unavailable')}</EmptyState>}
      {state === 'empty' && (
        <EmptyState>
          {t('myConsents.empty')}
          <EmptyHint>{t('myConsents.emptyHint')}</EmptyHint>
        </EmptyState>
      )}

      {state === 'ready' && (
        <CardGrid>
          {consents.map((c) => {
            const s = stateOf(c);
            return (
              <Card key={c.id}>
                <IconCircle><ShieldCheck size={20} /></IconCircle>
                <CardContent>
                  <CardTitle>{scopeLabel(c.scope)}</CardTitle>
                  <CardMeta>{t('myConsents.version')}: {c.version || '-'}</CardMeta>
                  <CardMeta>{t('myConsents.grantedAt')}: {c.grantedAt ? formatDateTime(c.grantedAt, lang) : '-'}</CardMeta>
                  {c.revokedAt && (
                    <CardMeta>{t('myConsents.revokedAt')}: {formatDateTime(c.revokedAt, lang)}</CardMeta>
                  )}
                  {c.revokedAt && c.revokeReason && s !== 'superseded' && (
                    <CardMeta>{t('myConsents.reason')}: {reasonLabel(c.revokeReason)}</CardMeta>
                  )}
                  <div><StatusBadge $state={s}>{t(`kvkkConsent.state.${s}`)}</StatusBadge></div>
                  {s === 'active' && (
                    <Button variant="outline" onClick={() => setRevokeTarget(c)}>{t('myConsents.revoke')}</Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </CardGrid>
      )}
    </Container>
  );
}
